/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

interface BatchConfirmModalProps {
  modalOpened: boolean;
  setModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  message: string;
  confirmLabel: string;
  successMessage: string;
  errorMessage: string;
  variant?: 'danger' | 'primary';
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  onConfirm: () => void;
  onSuccess?: () => void;
  confirmBlocked?: boolean;
  children?: React.ReactNode;
}

const BatchConfirmModal: React.FC<BatchConfirmModalProps> = ({
  modalOpened,
  setModalOpened,
  title,
  message,
  confirmLabel,
  successMessage,
  errorMessage,
  variant = 'danger',
  isLoading,
  isLoaded,
  isError,
  onConfirm,
  onSuccess,
  confirmBlocked = false,
  children,
}) => {
  const [actionDispatched, setActionDispatched] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const wasLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!actionDispatched) return;

    if (isLoading) {
      wasLoadingRef.current = true;
      return;
    }

    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;

    if (isLoaded) {
      setShowSuccess(true);
    } else if (isError) {
      setShowError(true);
    }
  }, [actionDispatched, isLoading, isLoaded, isError]);

  const confirmButtonClass = `btn btn-md btn-block btn-${variant}`;
  const isProcessing = actionDispatched && isLoading;

  const closeModal = () => {
    if (showSuccess && onSuccess) onSuccess();
    setModalOpened(false);
  };

  return (
    <Modal show={modalOpened} size="sm">
      <div className="modal-content">
        <div className="modal-header">
          <h6 className="modal-title">{title}</h6>
          <button aria-label="Close" className="close" type="button" onClick={closeModal}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">
          {showError && <div className="my-2 alert alert-danger text-center">{errorMessage}</div>}
          {showSuccess && (
            <div className="my-2 alert alert-success text-center">{successMessage}</div>
          )}
          {!showSuccess && (
            <div className="alert alert-warning" role="alert">
              {message}
            </div>
          )}
          {!showSuccess && children}
          <div className="form-row">
            {!showSuccess && (
              <button
                type="button"
                className={confirmButtonClass}
                disabled={isProcessing || confirmBlocked}
                onClick={() => {
                  setShowError(false);
                  onConfirm();
                  setActionDispatched(true);
                }}
              >
                {confirmLabel}
              </button>
            )}
            <button
              type="button"
              className="btn btn-md btn-block btn-secondary"
              disabled={isProcessing}
              onClick={closeModal}
            >
              {showSuccess ? 'Fechar' : 'Cancelar'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BatchConfirmModal;
